<mat-sidenav-container>
  <mat-sidenav #sidenav fixedInViewport="true" role="navigation" class="side-nav flex-layout column">
    <mat-nav-list>
      <a mat-list-item class="side-nav-button" routerLink="/order-shipment" routerLinkActive="side-nav-button-active">
        Order Shipment
      </a>

      @if (user().permissions.insightOperations) {
        <a mat-list-item class="side-nav-button" routerLink="/operational-dashboard" routerLinkActive="side-nav-button-active">
          Operational Dashboard
        </a>
      }

      <a mat-list-item class="side-nav-button" routerLink="/drive-delivery-status" routerLinkActive="side-nav-button-active">
        Drive Delivery Status
      </a>
    </mat-nav-list>
  </mat-sidenav>

  <mat-sidenav-content class="flex-layout column stretch-items" style="gap: 0">
    <mat-toolbar class="nav-toolbar flex-layout no-wrap">
      <button class="phone-only nav-button-menu" type="button" aria-label="Toggle sidenav" mat-icon-button (click)="sidenav.toggle()">
        <mat-icon aria-label="Side nav toggle icon">menu</mat-icon>
      </button>

      <a class="nav-branding" href="/">
        <img class="nav-logo" src="assets/swank-logo.png"/>
        <span class="branding-title">Insight</span>
      </a>

      @if (environmentName !== 'PROD') {
        <span class="environment-badge">{{ environmentName }}</span>
      }

      <a [matButton]="hasOrderShipmentsWarning() ? 'filled' : 'text'" [color]="hasOrderShipmentsWarning() ? 'warn' : null"
         class="nav-button desktop-only" [class.cache-not-ready]="hasOrderShipmentsWarning()"
         routerLink="/order-shipment" [routerLinkActive]="hasOrderShipmentsWarning() ? '' : 'nav-button-active'"
         #overlayOrigin="cdkOverlayOrigin" cdkOverlayOrigin>
        @if (hasOrderShipmentsWarning()) {
          <mat-icon>warning</mat-icon>
        }
        <span>Order Shipments</span>
        
        <insight-popup-tooltip [overlayOrigin]="overlayOrigin">
          <ng-template>
            <div class="order-shipment-info">
              <strong>There are two steps for getting all orders:</strong>
              <span>
                <strong>In databases</strong>, jobs are running that prepare a cache of order details
              </span>
              <span>
                <strong>In Insight</strong>, a final processing step that combines these into a single
                list in a format for display
              </span>

              <hr/>

              @if (allOrderShipmentGroupsList().nextCacheProcessingStartedTime !== null) {
                <insight-loading-spinner [loadingMessage]="formattedNextCacheProcessingStartedTime()">
                </insight-loading-spinner>

                <hr />
              }

              @if (allOrderShipmentGroupsList().lastCacheProcessingError !== null) {
                <div class="cache-error">
                  {{formattedLastCacheProcessingError()}}
                </div>

                <hr />
              }

              @if (isMissingContentStale()) {
                <span class="warning-text">
                  {{formattedMissingContentRefreshWarning()}}
                </span>

                <hr />
              }

              @if (allOrderShipmentGroupsList().cacheProcessingTime !== null) {
                <div class="value-list-item">
                  <strong>Final processing finished:</strong>
                  <span>{{formattedCacheProcessingTime()}}</span>
                </div>

                @if (formattedFinalProcessingTime() !== null) {
                  <div class="value-list-item">
                    <strong>Final processing took:</strong>
                    <span>{{formattedFinalProcessingTime()}}</span>
                  </div>
                }

                @if (formattedSGCCacheTime() !== null) {
                  <div class="value-list-item">
                    <strong>SGC database cache updated:</strong>
                    <span>{{formattedSGCCacheTime()}}</span>
                  </div>
                }

                @if (formattedADEBSCacheTime() !== null) {
                  <div class="value-list-item">
                    <strong>ADEBS database cache updated:</strong>
                    <span>{{formattedADEBSCacheTime()}}</span>
                  </div>
                }

                <div class="value-list-item">
                  <strong>Max order details per database:</strong>
                  <span>{{formattedMaxAllOrderShipments()}}</span>
                </div>

                <div class="value-list-item" [class.hitting-limit]="isSGCAtLimit()">
                  <strong>Total SGC order details:</strong>
                  <span>{{formattedSGCCount()}}</span>
                </div>

                <div class="value-list-item" [class.hitting-limit]="isADEBSAtLimit()">
                  <strong>Total ADEBS order details:</strong>
                  <span>{{formattedADEBSCount()}}</span>
                </div>
              } @else {
                <span class="warning-text">
                  The order shipments have not finished final processing yet.
                  Missing content is calculated from this cache, so orders will not show
                  missing content until this finishes.
                </span>
              }
            </div>
          </ng-template>
        </insight-popup-tooltip>
      </a>

      @if (user().permissions.insightOperations) {
        <a mat-button class="nav-button desktop-only" routerLink="/operational-dashboard" routerLinkActive="nav-button-active">
          Operational Dashboard
        </a>
      }

      <a mat-button class="nav-button desktop-only" routerLink="/drive-delivery-status" routerLinkActive="nav-button-active">
        Drive Delivery Status
      </a>      

      <!-- Only show this (CvdDongle) if there are alerts to show with low keys/expiring soon workstations-->
      @if (cvdAlertMessages.length > 0 && (user().permissions.insightOperations || user().permissions.insightEditor)) {
        <span>
          <button matButton="filled" color="warn" class="origin" #overlayOrigin="cdkOverlayOrigin" cdkOverlayOrigin>
            <mat-icon>warning</mat-icon>
            <span>{{ cvdAlertMessages.length }} CVD Dongle <span class="on-large-window">Warning{{ cvdAlertMessages.length > 1 ? "s" : "" }}</span></span>
          </button>
          <insight-popup-tooltip [overlayOrigin]="overlayOrigin">
            <ng-template>
              <ul class="cvd-messages">
                @for (message of cvdAlertMessages; track message) {
                  <li>
                    {{ message }}
                  </li>
                }
              </ul>
            </ng-template>
          </insight-popup-tooltip>
        </span>
      }

      <!-- Only show cache warning if there are alerts to show about stale cache data -->
      @if (cacheMessages().outdatedWarnings.length > 0 || cacheMessages().nullWarnings.length > 0) {
        <span>
          <button matButton="filled" color="warn" class="origin" #overlayOrigin="cdkOverlayOrigin" cdkOverlayOrigin>
            <mat-icon>warning</mat-icon>
            <span>
              {{ cacheMessages().outdatedWarnings.length + cacheMessages().nullWarnings.length }} Stale Cache <span class="on-large-window">Warning{{
                cacheMessages().outdatedWarnings.length + cacheMessages().nullWarnings.length > 1 ? "s" : ""
              }}</span>
            </span>
          </button>
          <insight-popup-tooltip [overlayOrigin]="overlayOrigin">
            <ng-template>
              Insight data is updated on a timed schedule and stored in a cache.
              <br />
              @if (cacheMessages().outdatedWarnings.length > 0) {
                @for (error of cacheMessages().outdatedWarnings; track error.name) {
                  <ul class="cvd-messages">
                    <li>
                      The <strong>{{ error.name }}</strong> data cache was last updated on
                      <strong>{{ error.lastRefresh | date: "medium" }}</strong>
                      ({{ timeService.stringifyMinutes(error.ageMinutes) }} ago)
                    </li>
                  </ul>
                }
                <span class="warning-text">
                  It has been over {{ MAXIMUM_CACHE_AGE_MINUTES }} minutes since
                  {{ cacheMessages().outdatedWarnings.length > 1 ? "these" : "this" }} data refresh{{
                    cacheMessages().outdatedWarnings.length > 1 ? "es" : ""
                  }}
                  happened. The data is stale.
                  <br />
                  Please submit an application support ticket with IT to look into the issue.
                </span>
              }
              @if (cacheMessages().nullWarnings.length > 0) {
                @for (error of cacheMessages().nullWarnings; track error.name) {
                  <ul class="cvd-messages">
                    <li>
                      There was a problem retrieving the <strong>{{ error.name }}</strong> data cache's last refresh time.
                      @if (error.name === "ADEBSDB") {
                        <!-- ADEBSDB still uses the old refresh time storage method which fails when the cache is empty. -->
                        <br />
                        (This could be caused by an empty data cache.)
                      }
                    </li>
                  </ul>
                }
                <span class="warning-text">
                  If {{ cacheMessages().nullWarnings.length > 1 ? "these" : "this" }} persist{{
                    cacheMessages().nullWarnings.length > 1 ? "" : "s"
                  }}, please submit an application support ticket with IT to look into the issue{{
                    cacheMessages().nullWarnings.length > 1 ? "s" : ""
                  }}.
                </span>
              }
            </ng-template>
          </insight-popup-tooltip>
        </span>
      }

      <span class="item-grow"></span>

      <div class="right-links">
        <span>{{ user().name }}</span>
        <a class="button-link" href="https://swankmp.clickhelp.co/articles/internal-insight-publication" target="_blank">
          <mat-icon>help</mat-icon>
          <span>Help</span>
        </a>

        <a class="button-link guide-backup" target="_blank" href="https://swankmp.atlassian.net/wiki/spaces/ISFS/pages/4176773133/Insight+Help+and+Error+Guides">
          <span>(Backup)</span>
        </a>

        <button class="button-link" (click)="signOut()">Sign Out</button>
        <span class="version-number">{{ applicationVersion() }}</span>
      </div>
    </mat-toolbar>

    <div>
      <router-outlet></router-outlet>
    </div>
  </mat-sidenav-content>
</mat-sidenav-container>
